/**
 * Commissions Hooks
 *
 * React Query hooks for the Easel commissions pipeline.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { commissionsApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'
import type { CommissionSource, CommissionStatus, InvoiceKind, UpdateCommissionData } from '../types'

// =============================================================================
// Query Keys
// =============================================================================

export const commissionKeys = {
  all: ['commissions'] as const,
  lists: () => [...commissionKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...commissionKeys.lists(), filters] as const,
  details: () => [...commissionKeys.all, 'detail'] as const,
  detail: (id: number) => [...commissionKeys.details(), id] as const,
}

// =============================================================================
// Commission Hooks
// =============================================================================

export function useCommissionsAccess(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...commissionKeys.all, 'access'] as const,
    queryFn: () => commissionsApi.getAccess(),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useCommissions(params?: {
  status?: CommissionStatus
  source?: CommissionSource
  limit?: number
  offset?: number
}) {
  return useQuery({
    queryKey: commissionKeys.list(params || {}),
    queryFn: () => commissionsApi.getCommissions(params),
  })
}

export function useCommission(id: number) {
  return useQuery({
    queryKey: commissionKeys.detail(id),
    queryFn: () => commissionsApi.getCommission(id),
    enabled: !!id,
  })
}

export function usePromoteInquiry() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ inquiryId, source }: { inquiryId: number; source?: CommissionSource }) =>
      commissionsApi.promoteInquiry(inquiryId, source),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.lists() })
      toast({
        title: 'Commission Created',
        description: 'The inquiry was promoted to a commission.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create commission',
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateCommission() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCommissionData }) =>
      commissionsApi.updateCommission(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: commissionKeys.lists() })
      toast({
        title: 'Commission Updated',
        description: 'Your changes have been saved.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update commission',
        variant: 'destructive',
      })
    },
  })
}

export function useCreateInvoice() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      id,
      kind,
      shippingTitle,
      shippingAmount,
    }: {
      id: number
      kind: InvoiceKind
      shippingTitle?: string
      shippingAmount?: number
    }) => commissionsApi.createInvoice(id, kind, { shippingTitle, shippingAmount }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: commissionKeys.lists() })
      toast({
        title: 'Invoice Created',
        description: 'A Shopify checkout link is ready for the patron.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create invoice',
        variant: 'destructive',
      })
    },
  })
}

export function useReconciliation() {
  return useQuery({
    queryKey: [...commissionKeys.all, 'reconciliation'] as const,
    queryFn: () => commissionsApi.getReconciliation(),
  })
}

export function useRefundCommission() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      commissionsApi.refundCommission(id, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: commissionKeys.all })
      toast({ title: 'Commission Refunded', description: 'The commission was marked refunded.' })
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to refund', variant: 'destructive' })
    },
  })
}

export function useApproveDraft() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, messageId }: { id: number; messageId: number }) =>
      commissionsApi.approveDraft(id, messageId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.detail(variables.id) })
      toast({ title: 'Reply Sent', description: 'The draft was approved and sent to the patron.' })
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to approve draft', variant: 'destructive' })
    },
  })
}

export function useDismissDraft() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, messageId }: { id: number; messageId: number }) =>
      commissionsApi.dismissDraft(id, messageId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.detail(variables.id) })
      toast({ title: 'Draft Dismissed', description: 'The suggested reply was dismissed.' })
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to dismiss draft', variant: 'destructive' })
    },
  })
}

export function useCreateCommissionMessage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, bodyText }: { id: number; bodyText: string }) =>
      commissionsApi.createMessage(id, bodyText),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.detail(variables.id) })
      toast({
        title: 'Message Sent',
        description: 'Your reply was sent to the patron.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message',
        variant: 'destructive',
      })
    },
  })
}
